/**
 * Exact decimal-string arithmetic on BigInt. Money never touches floating point:
 * `mulDecimal("46.12", "0.9213", 2)` -> "42.49".
 */

const DEC = /^-?\d+(\.\d+)?$/;

export function isDecimal(s: unknown): s is string {
  return typeof s === 'string' && DEC.test(s.trim());
}

/** Split a decimal string into a scaled BigInt and its scale (number of fraction digits). */
function parse(s: string): { v: bigint; scale: number } {
  const t = s.trim();
  if (!DEC.test(t)) throw new Error(`not a decimal string: ${s}`);
  const neg = t.startsWith('-');
  const [whole, frac = ''] = (neg ? t.slice(1) : t).split('.');
  const v = BigInt(whole + frac);
  return { v: neg ? -v : v, scale: frac.length };
}

/** Round a scaled BigInt (scale `from`) to `to` fraction digits, half away from zero. */
function rescale(v: bigint, from: number, to: number): bigint {
  if (to >= from) return v * 10n ** BigInt(to - from);
  const div = 10n ** BigInt(from - to);
  const q = v / div;
  const r = v % div;
  const abs = r < 0n ? -r : r;
  if (abs * 2n >= div) return v < 0n ? q - 1n : q + 1n;
  return q;
}

function render(v: bigint, scale: number): string {
  const neg = v < 0n;
  const s = (neg ? -v : v).toString().padStart(scale + 1, '0');
  const whole = s.slice(0, s.length - scale);
  const frac = s.slice(s.length - scale);
  return `${neg ? '-' : ''}${whole}${scale ? `.${frac}` : ''}`;
}

/** a × b, rounded to `digits` fraction digits (half away from zero). */
export function mulDecimal(a: string, b: string, digits: number): string {
  const x = parse(a);
  const y = parse(b);
  return render(rescale(x.v * y.v, x.scale + y.scale, digits), digits);
}

/** Round a decimal string to `digits` fraction digits. */
export function roundDecimal(a: string, digits: number): string {
  const x = parse(a);
  return render(rescale(x.v, x.scale, digits), digits);
}
